/**
 * Shared <PromptEditor scope> — Prompts authoring surface.
 *
 * PR-E: prompt-editor shared-component refactor (2026-05-12).
 *
 * Mounted by:
 *   - routes/studio/prompts.tsx              (scope="tenant")
 *   - routes/sysadmin/catalog.prompts.tsx    (scope="sysadmin")
 *
 * Both surfaces are slash-command prompt-template editors with:
 *   - a search-filter row
 *   - a flat table list (Command / Title / Category / … / Actions)
 *   - a Sheet with the create/edit form (command, title, description,
 *     content, typed variables, category)
 *   - a confirm-delete AlertDialog
 *
 * The shared body renders both. Scope-specific affordances are gated
 * behind adapter flags (Bucket B). Save payload shape (Bucket C) is
 * built by the adapter's `serialize{Create,Update}Payload`.
 *
 * ─── SCOPE-DEVIATION TABLE ────────────────────────────────────────────────
 *
 *   Bucket A — Hook swaps (clean, identical row payloads except 1 field)
 *   ──────────────────────────────────────────────────────────────────────
 *     tenant   : usePromptTemplates             → GET /templates
 *                useCreatePromptTemplate        → POST /templates
 *                useUpdatePromptTemplate        → PUT /templates/:id
 *                useDeletePromptTemplate        → DELETE /templates/:id
 *                useClonePromptFromSystem       → POST /templates/clone-from-system/:id
 *                  (returns PromptTemplate; flat array under data.data)
 *     sysadmin : useSysadminPrompts             → GET  /admin/platform/studio-prompts
 *                useCreateSysadminPrompt        → POST /admin/platform/studio-prompts
 *                useUpdateSysadminPrompt        → PATCH /admin/platform/studio-prompts/:id
 *                useDeleteSysadminPrompt        → DELETE /admin/platform/studio-prompts/:id
 *                useToggleSysadminPromptVisibility → PATCH …/:id/visibility
 *                  (returns SysadminPrompt; flat array under data.items)
 *
 *   Bucket B — Affordances rendered only on one side
 *   ─────────────────────────────────────────────────
 *     Tenant only   : Scope filter chips (all / tenant / system / cloned)
 *                     Category dropdown (auto-built from list)
 *                     Source column (System / Cloned / Yours badge)
 *                     Eye + Clone actions for read-only "System" rows
 *                     Prompt Helper inline (Collapsible HelperPanel
 *                       kind="prompt_helper") with set_metadata /
 *                       set_template_body / set_variables mutations
 *                     PromptPreviewPane (live {{var}} substitution)
 *                     Provenance banner when editing a cloned-from-
 *                       system row (Round 3 fix #146)
 *                     "variablesHaveBlankNames" save-gate + tooltip
 *                       (Round 6 fix STUDIO-R6-002)
 *                     Typed VariableEditor in the edit sheet (sysadmin's
 *                       pre-refactor surface had no variable editor;
 *                       gated via showVariableEditor for parity)
 *                     "{n} prompts" row count chip on the right of the
 *                       search row (gated via showRowCount, promoted
 *                       from a scope === check in PR-E audit L-2)
 *     Sysadmin only : Card header with "§3.3 v1" badges
 *                     "Visible to tenant" column with Switch toggle +
 *                       inline "System" pill when on
 *                     "Updated" timestamp column
 *                     "Visible to tenant" Switch row inside the sheet
 *                     "New prompt" button placed at the right of the
 *                       search row (tenant places it in the page header);
 *                       gated via newButtonPlacement, promoted from a
 *                       scope === check in PR-E audit L-2
 *
 *   Bucket B — Promoted scope flags (PR-E audit L-2 follow-up)
 *   ───────────────────────────────────────────────────────────
 *     The following five `scope === 'sysadmin'` / `scope === 'tenant'`
 *     direct checks were promoted to named adapter constants so the body
 *     never inspects scope directly for them (matches PR-A/B Bucket B
 *     discipline):
 *       emptyDraft()        — was: scope === 'sysadmin' ? EMPTY_SYSADMIN_DRAFT : EMPTY_DRAFT
 *       HeadingIcon         — was: scope === 'sysadmin' ? FileType : MessageSquare
 *       newButtonPlacement  — was: scope === 'sysadmin' guard around the
 *                                 search-row "New prompt" button
 *       showRowCount        — was: scope === 'tenant' guard around the
 *                                 "{n} prompts" count chip
 *       draftFromRow(p)     — was: visibleToTenant: scope === 'sysadmin'
 *                                 ? p.visibleToTenant : undefined inside
 *                                 the Edit button's onClick
 *
 *   Bucket C — Save payload shape
 *   ──────────────────────────────
 *     Both bodies have: { command, title, description?, content,
 *                         variables, category }
 *     Sysadmin adds:    visibleToTenant: boolean
 *     (Variable shape is IDENTICAL — name, label, type, required, default,
 *      options — see PromptVariableEntry.)
 *
 *   Bucket D — Logic structure
 *   ──────────────────────────
 *     Save / delete handlers are structurally identical (try/catch +
 *     mutateAsync + toast). They differ only in which adapter mutation
 *     they call. Wrapped behind `adapter.useSave()` + `adapter.useDelete()`.
 *
 * Every deviation above is funnelled through the adapter at
 * `./PromptEditor.adapters.tsx`. The body below is scope-pure — it
 * conditionally renders only on `adapter.show*` flags + scope-typed
 * mutation hooks.
 */
import { useState, useMemo, type ReactNode } from 'react';
import {
  Search, AlertTriangle, Plus, Pencil, Trash2, X, Eye, Copy,
  ExternalLink,
  ShieldCheck, Sparkles as SparklesIcon,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  VariableEditor,
  VariableValueForm,
  previewPromptTemplate,
  variablesHaveBlankNames,
  type PromptVariableEntry,
} from '@/components/studio';
import { HelperPanel } from '@/components/studio/helper-panel';
import type { PendingMutation } from '@/lib/use-studio-helper';
import { useHasPermission } from '@/lib/use-has-permission';

import { getAdapter } from './PromptEditor.adapters';
import {
  applyPromptHelperMutation,
  rowScope,
} from './PromptEditor.helpers';
import type {
  NormalizedPrompt,
  PromptDraft,
  PromptEditorScope,
} from './PromptEditor.types';

interface PromptEditorProps {
  scope: PromptEditorScope;
}

export function PromptEditor({ scope }: PromptEditorProps) {
  const adapter = getAdapter(scope);
  const { prompts, loading, error } = adapter.useList();
  const { saveInFlight, save } = adapter.useSave();
  const del = adapter.useDelete();
  const toggleVis = adapter.useToggleVisibility();
  const clone = adapter.useCloneFromSystem();
  const canWritePrompts = useHasPermission('write', 'prompts');
  const canDeletePrompts = useHasPermission('delete', 'prompts');
  const canCreateOrEdit = scope === 'sysadmin' || canWritePrompts;
  const canDelete = scope === 'sysadmin' || canDeletePrompts;

  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<PromptDraft | null>(null);
  const [viewSystem, setViewSystem] = useState<NormalizedPrompt | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<NormalizedPrompt | null>(null);
  // Tenant-only: scope filter chips + category dropdown (Round 3 fix #146).
  const [scopeFilter, setScopeFilter] = useState<
    'all' | 'system' | 'tenant' | 'cloned'
  >('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const categories = useMemo(() => {
    if (!adapter.showCategoryDropdown) return [];
    return Array.from(new Set(prompts.map((p) => p.category ?? 'general'))).sort();
  }, [prompts, adapter.showCategoryDropdown]);

  const filtered = useMemo(() => {
    let rows = prompts;
    if (adapter.showScopeChips && scopeFilter !== 'all') {
      rows = rows.filter((p) => rowScope(p) === scopeFilter);
    }
    if (adapter.showCategoryDropdown && categoryFilter !== 'all') {
      rows = rows.filter((p) => (p.category ?? 'general') === categoryFilter);
    }
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (p) =>
        p.command.toLowerCase().includes(q) ||
        p.title.toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q) ||
        (p.category ?? '').toLowerCase().includes(q),
    );
  }, [
    prompts,
    search,
    scopeFilter,
    categoryFilter,
    adapter.showScopeChips,
    adapter.showCategoryDropdown,
  ]);

  const isEdit = draft?.id !== undefined;

  const handleSave = () => {
    if (!draft) return;
    if (!canCreateOrEdit) {
      toast.error('Permission required', {
        description: 'You can view Studio prompts, but cannot create or edit them.',
      });
      return;
    }
    const body = isEdit
      ? adapter.serializeUpdatePayload(draft)
      : adapter.serializeCreatePayload(draft);
    save({
      body,
      isEdit,
      id: draft.id,
      onSuccess: () => {
        toast.success(
          isEdit ? `Updated "/${draft.command}"` : `Created "/${draft.command}"`,
        );
        setDraft(null);
      },
      onError: (err) =>
        toast.error(isEdit ? 'Update failed' : 'Create failed', {
          description: err.message,
        }),
    });
  };

  const handleDelete = (p: NormalizedPrompt) => {
    if (!canDelete) {
      toast.error('Permission required', {
        description: 'You can view Studio prompts, but cannot delete them.',
      });
      setConfirmDelete(null);
      return;
    }
    del.run({
      id: p.id,
      onSuccess: () => toast.success(`Deleted "/${p.command}"`),
      onError: (err) => toast.error('Delete failed', { description: err.message }),
    });
    setConfirmDelete(null);
  };

  const handleToggleVis = (p: NormalizedPrompt) => {
    if (!toggleVis) return;
    toggleVis.run({
      id: p.id,
      visibleToTenant: !p.visibleToTenant,
      onSuccess: () =>
        toast.success(
          !p.visibleToTenant
            ? `"/${p.command}" is now visible to tenants`
            : `"/${p.command}" is now sysadmin-only`,
        ),
      onError: (err) =>
        toast.error('Toggle failed', { description: err.message }),
    });
  };

  const handleClone = (p: NormalizedPrompt) => {
    if (!clone) return;
    if (!canCreateOrEdit) {
      toast.error('Permission required', {
        description: 'You can view system prompts, but cannot clone them.',
      });
      return;
    }
    clone.run({
      id: p.id,
      onSuccess: (resp) => {
        const newCommand =
          (resp as { data?: { command?: string } } | undefined)?.data?.command ??
          p.command;
        toast.success(`Cloned to /${newCommand}`, {
          description: 'Editable copy added to your prompts.',
        });
      },
      onError: (err) => toast.error('Clone failed', { description: err.message }),
    });
  };

  // ─── Render helpers ─────────────────────────────────────────────────

  const renderSourceBadge = (p: NormalizedPrompt): ReactNode => {
    if (p.isSystem) {
      return (
        <Badge
          variant="default"
          className="text-[10px] gap-1 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20"
        >
          <ShieldCheck className="h-2.5 w-2.5" />
          System
        </Badge>
      );
    }
    if (p.clonedFromSystemId) {
      return (
        <Badge
          variant="outline"
          className="text-[10px] gap-1 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30"
        >
          <Copy className="h-2.5 w-2.5" />
          Cloned
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="text-[10px]">
        Yours
      </Badge>
    );
  };

  // Icon used in the page heading. Selected by the adapter
  // (`adapter.HeadingIcon`) — promoted from a `scope ===` ternary in
  // PR-E audit L-2.
  const HeadingIcon = adapter.HeadingIcon;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      {/* Sysadmin: header w/ §3.3 badges. Tenant: inline h2 + new button. */}
      {scope === 'sysadmin' ? (
        <>
          <div className="flex items-center gap-3">
            <HeadingIcon className="h-7 w-7 text-muted-foreground" />
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                {adapter.pageTitle}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs font-mono">§3.3</Badge>
                <Badge variant="secondary" className="text-xs">v1</Badge>
              </div>
            </div>
          </div>
          {adapter.showCardHeader && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Catalog</CardTitle>
                <CardDescription>{adapter.pageSubtitle}</CardDescription>
              </CardHeader>
            </Card>
          )}
        </>
      ) : (
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2">
              <HeadingIcon className="h-4 w-4 text-muted-foreground" />
              {adapter.pageTitle}
            </h2>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              {adapter.pageSubtitle}
            </p>
          </div>
          {canCreateOrEdit && (
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => setDraft(adapter.emptyDraft())}
            >
              <Plus className="h-3.5 w-3.5" /> New Prompt
            </Button>
          )}
        </div>
      )}

      {/* Search + scope/category filters row */}
      <div className="flex items-center gap-2 flex-wrap">
        <div
          className={
            scope === 'sysadmin'
              ? 'relative flex-1 max-w-sm'
              : 'relative flex-1 min-w-[220px] max-w-sm'
          }
        >
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search prompts…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
        </div>

        {/* Tenant: scope chips (Round 3 fix #146). */}
        {adapter.showScopeChips && (
          <div className="flex items-center gap-1.5">
            {(['all', 'tenant', 'system', 'cloned'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setScopeFilter(s)}
                className={`px-2 py-1 text-[11px] rounded border transition-colors capitalize ${
                  scopeFilter === s
                    ? s === 'system'
                      ? 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30'
                      : s === 'cloned'
                        ? 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30'
                        : s === 'tenant'
                          ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30'
                          : 'bg-foreground text-background border-foreground'
                    : 'bg-transparent text-muted-foreground border-border hover:text-foreground'
                }`}
              >
                {s === 'all' ? 'All scopes' : s}
              </button>
            ))}
          </div>
        )}

        {/* Tenant: category dropdown when >1 category. */}
        {adapter.showCategoryDropdown && categories.length > 1 && (
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="h-7 text-[11px] rounded border bg-background px-2 text-foreground"
            aria-label="Filter by category"
          >
            <option value="all">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        )}

        {/* New-button at the right of the search row when adapter says so
            (sysadmin). Tenant renders this button in the page header
            instead. Promoted from a `scope === 'sysadmin'` check in
            PR-E audit L-2. */}
        {adapter.newButtonPlacement === 'search-row' && canCreateOrEdit && (
          <Button
            size="sm"
            className="gap-1.5 ml-auto"
            onClick={() => setDraft(adapter.emptyDraft())}
          >
            <Plus className="h-3.5 w-3.5" /> New Prompt
          </Button>
        )}

        {/* Row count at the right (tenant). Promoted from a
            `scope === 'tenant'` check in PR-E audit L-2. */}
        {adapter.showRowCount && prompts.length > 0 && (
          <span className="text-xs text-muted-foreground tabular-nums ml-auto">
            {filtered.length} {filtered.length === 1 ? 'prompt' : 'prompts'}
          </span>
        )}
      </div>

      {/* List body: loading / error / empty / table */}
      {loading ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground animate-pulse">
            Loading prompts…
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              {scope === 'sysadmin' ? 'Failed to load:' : 'Failed to load prompts:'}{' '}
              {error.message}
            </div>
          </CardContent>
        </Card>
      ) : prompts.length === 0 ? (
        // fix-wave-4 #1035 (STUDIO-PROMPTS-002) — Pre-fix the empty
        // state hid the table entirely so the column header contract
        // disappeared. Render the canonical header row + an empty-state
        // body cell so accessibility queries / matrix probes still see
        // Command / Title / Category / Source on every paint.
        <Card>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-44">Command</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="w-28">Category</TableHead>
                  {adapter.showSourceColumn && (
                    <TableHead className="w-28">Source</TableHead>
                  )}
                  {adapter.showVisibleToTenantColumn && (
                    <TableHead className="w-32">Visible to tenant</TableHead>
                  )}
                  <TableHead className="w-24">Variables</TableHead>
                  {adapter.showUpdatedColumn && (
                    <TableHead className="w-44">Updated</TableHead>
                  )}
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="py-10 text-center text-sm text-muted-foreground"
                  >
                    {adapter.emptyStateMessage}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-44">Command</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="w-28">Category</TableHead>
                  {adapter.showSourceColumn && (
                    <TableHead className="w-28">Source</TableHead>
                  )}
                  {adapter.showVisibleToTenantColumn && (
                    <TableHead className="w-32">Visible to tenant</TableHead>
                  )}
                  <TableHead className="w-24">Variables</TableHead>
                  {adapter.showUpdatedColumn && (
                    <TableHead className="w-44">Updated</TableHead>
                  )}
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-xs">/{p.command}</TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{p.title}</div>
                      {p.description && (
                        <div className="text-[11px] text-muted-foreground line-clamp-1 max-w-md">
                          {p.description}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">
                        {p.category ?? 'general'}
                      </Badge>
                    </TableCell>

                    {adapter.showSourceColumn && (
                      <TableCell>{renderSourceBadge(p)}</TableCell>
                    )}

                    {adapter.showVisibleToTenantColumn && (
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={p.visibleToTenant}
                            disabled={toggleVis?.inFlight ?? false}
                            onCheckedChange={() => handleToggleVis(p)}
                          />
                          {p.visibleToTenant && (
                            <Badge
                              variant="default"
                              className="text-[10px] gap-1 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20"
                            >
                              <ShieldCheck className="h-2.5 w-2.5" />
                              System
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    )}

                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                      {p.variables.length}
                    </TableCell>

                    {adapter.showUpdatedColumn && (
                      <TableCell className="text-xs text-muted-foreground tabular-nums">
                        {p.updatedAt ? new Date(p.updatedAt).toLocaleString() : '—'}
                      </TableCell>
                    )}

                    <TableCell className="text-right">
                      {adapter.showSystemRowActions && p.isSystem ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            title={`View /${p.command}`}
                            aria-label={`View prompt /${p.command}`}
                            className="h-7 w-7 p-0"
                            onClick={() => setViewSystem(p)}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          {canCreateOrEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title={`Clone & customize /${p.command}`}
                              aria-label={`Clone & customize prompt /${p.command}`}
                              className="h-7 w-7 p-0 text-violet-600 dark:text-violet-300 hover:bg-violet-500/10"
                              disabled={clone?.inFlight ?? false}
                              onClick={() => handleClone(p)}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          {canCreateOrEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title={`Quick edit /${p.command}`}
                              aria-label={`Quick edit prompt /${p.command}`}
                              className="h-7 w-7 p-0"
                              onClick={() => setDraft(adapter.draftFromRow(p))}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {/* #153 — Open full detail page (tenant only).
                              The Sheet is for quick edits; the detail page
                              at /studio/prompts/:id is the canonical surface
                              for typed variable authoring, live preview,
                              and "Used by Agents" lookup. */}
                          {scope === 'tenant' && (
                            <Link
                              to="/studio/prompts/$promptId"
                              params={{ promptId: p.id }}
                              title={`Open /${p.command} in full editor`}
                              aria-label={`Open prompt /${p.command} in full editor`}
                              className="inline-flex items-center justify-center h-7 w-7 rounded-md text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Link>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title={`Delete /${p.command}`}
                              aria-label={`Delete prompt /${p.command}`}
                              className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                              onClick={() => setConfirmDelete(p)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Edit / Create sheet */}
      <Sheet open={draft !== null} onOpenChange={(open) => !open && setDraft(null)}>
        <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{isEdit ? 'Edit prompt' : adapter.newSheetTitle}</SheetTitle>
            <SheetDescription>{adapter.sheetDescription}</SheetDescription>
          </SheetHeader>
          {draft && (
            <div className="space-y-4 mt-4">
              {/* Provenance banner — tenant only, when editing a cloned row. */}
              {adapter.showProvenanceBanner && draft.clonedFromSystemId && (
                <div className="rounded border border-cyan-500/30 bg-cyan-500/5 px-3 py-2 text-xs text-cyan-700 dark:text-cyan-300 flex items-start gap-2">
                  <Copy className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <div>
                    <strong>Cloned from a system prompt.</strong> You can edit
                    this copy freely; the original system prompt is unchanged.
                    Provenance UUID:{' '}
                    <code className="font-mono">{draft.clonedFromSystemId}</code>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="pe-command" className="text-xs">
                    Command (slug)
                  </Label>
                  <Input
                    id="pe-command"
                    value={draft.command}
                    onChange={(e) =>
                      setDraft({ ...draft, command: e.target.value })
                    }
                    placeholder={adapter.commandPlaceholder}
                    disabled={isEdit || !canCreateOrEdit}
                    className="font-mono text-xs h-8"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pe-category" className="text-xs">
                    Category
                  </Label>
                  <Input
                    id="pe-category"
                    value={draft.category}
                    onChange={(e) =>
                      setDraft({ ...draft, category: e.target.value })
                    }
                    placeholder="general"
                    disabled={!canCreateOrEdit}
                    className="text-xs h-8"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="pe-title" className="text-xs">Title</Label>
                <Input
                  id="pe-title"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder={adapter.titlePlaceholder}
                  disabled={!canCreateOrEdit}
                  className="text-sm h-8"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="pe-description" className="text-xs">
                  Description (optional)
                </Label>
                <Textarea
                  id="pe-description"
                  value={draft.description}
                  onChange={(e) =>
                    setDraft({ ...draft, description: e.target.value })
                  }
                  placeholder={adapter.descriptionPlaceholder}
                  rows={2}
                  disabled={!canCreateOrEdit}
                  className="text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="pe-content" className="text-xs">
                  Content (Markdown / template)
                </Label>
                <Textarea
                  id="pe-content"
                  value={draft.content}
                  onChange={(e) =>
                    setDraft({ ...draft, content: e.target.value })
                  }
                  placeholder="The expanded prompt body. Use {{variable_name}} for substitutions."
                  rows={10}
                  disabled={!canCreateOrEdit}
                  // fix-wave-4 #1032 (STUDIO-PROMPTS-042) — Pre-fix the
                  // textarea had no max-height, so a 20k-character paste
                  // grew the element to fill the Sheet vertically. The
                  // Sheet's outer scroll could not catch up because the
                  // textarea was itself the largest child. The dialog
                  // either had its bottom controls pushed off-screen or
                  // (Radix focus-trap edge case) lost visibility entirely
                  // when the layout reflowed. Capping at max-h-72 lets
                  // the textarea's own scrollbar own the overflow while
                  // keeping the Save / Cancel row reachable.
                  className="text-sm font-mono max-h-72 resize-y"
                />
              </div>

              {/* Typed variable editor — tenant only (sysadmin's pre-refactor surface had no variable editor; parity-preserving). */}
              {adapter.showVariableEditor && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Variables</Label>
                  <VariableEditor
                    flavor="prompt"
                    value={draft.variables}
                    onChange={(next) =>
                      setDraft({ ...draft, variables: next as PromptVariableEntry[] })
                    }
                    disabled={!canCreateOrEdit}
                  />
                </div>
              )}

              {/* Preview pane — tenant only. */}
              {adapter.showPreviewPane && (
                <PromptPreviewPane
                  template={draft.content}
                  variables={draft.variables}
                />
              )}

              {/* Prompt Helper — tenant only. */}
              {adapter.showHelperPanel && (
                <Collapsible className="rounded border bg-violet-500/5">
                  <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-violet-700 dark:text-violet-300 hover:bg-violet-500/10 rounded">
                    <SparklesIcon className="h-3.5 w-3.5" />
                    Draft with Prompt Helper (AI)
                  </CollapsibleTrigger>
                  <CollapsibleContent className="border-t">
                    <HelperPanel
                      kind="prompt_helper"
                      intro="Tell me what slash-command prompt you need. I'll ask a few clarifying questions, then draft the template body and typed variables — you Accept or Reject each suggestion."
                      className="border-0 rounded-none min-h-[320px]"
                      canvasState={{
                        command: draft.command,
                        title: draft.title,
                        description: draft.description,
                        content: draft.content,
                        variables: draft.variables,
                        category: draft.category,
                      } as Record<string, unknown>}
                      onApplyMutation={(m: PendingMutation) => {
                        // #1094 — Pre-fix this handler called
                        //   setDraft({ ...draft, command: ... });
                        //   setDraft({ ...draft, title: ... });
                        //   setDraft({ ...draft, description: ... });
                        //   setDraft({ ...draft, category: ... });
                        // in sequence. Each `setDraft` captured the SAME
                        // stale `draft` from the React closure, so only the
                        // last call's field landed and every other field
                        // stayed blank. Symptom: helper streams a
                        // `set_metadata` card with command/title/description/
                        // category all set, user clicks Accept, the card
                        // disappears, only one of the four fields is
                        // populated, Create button stays disabled.
                        //
                        // Fix: compute a single Partial<PromptDraft> patch
                        // and apply it in ONE functional setState — never
                        // reads stale closure. Logic extracted to
                        // `applyPromptHelperMutation` so it can be
                        // exhaustively unit-tested without RTL/JSDOM.
                        const patch = applyPromptHelperMutation(
                          m.toolName,
                          m.args as Record<string, unknown>,
                        );
                        if (Object.keys(patch).length === 0) return;
                        setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
                      }}
                    />
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* Visible-to-tenant Switch — sysadmin only. */}
              {adapter.showVisibleToTenantSwitch && (
                <div className="flex items-center justify-between rounded border p-2">
                  <div>
                    <div className="text-sm">Visible to tenant</div>
                    <div className="text-[11px] text-muted-foreground">
                      Surfaces in every tenant's Studio Prompts tab as a
                      read-only "System" entry.
                    </div>
                  </div>
                  <Switch
                    checked={!!draft.visibleToTenant}
                    onCheckedChange={(v) =>
                      setDraft({ ...draft, visibleToTenant: v })
                    }
                  />
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setDraft(null)}>
                  <X className="h-3.5 w-3.5 mr-1" />
                  Cancel
                </Button>
                {/* Tenant: Round 6 fix STUDIO-R6-002 — block save when any
                    variable name is blank. Sysadmin had the same bug
                    structurally but the pre-refactor file didn't guard
                    it; the shared body uses the same guard now (extra
                    safety on sysadmin is intentional, not a regression). */}
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={
                    !draft.command ||
                    !draft.title ||
                    !draft.content ||
                    (scope === 'tenant' && variablesHaveBlankNames(draft.variables)) ||
                    saveInFlight ||
                    !canCreateOrEdit
                  }
                  title={
                    scope === 'tenant' && variablesHaveBlankNames(draft.variables)
                      ? 'One or more variables are missing a name.'
                      : undefined
                  }
                >
                  {saveInFlight
                    ? 'Saving…'
                    : isEdit
                      ? 'Save changes'
                      : 'Create prompt'}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* View-only sheet for system prompts — tenant only. */}
      {adapter.showSystemRowActions && (
        <Sheet
          open={viewSystem !== null}
          onOpenChange={(open) => !open && setViewSystem(null)}
        >
          <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle>System prompt</SheetTitle>
              <SheetDescription>
                This prompt is authored by your platform administrator. It's
                read-only here — you can use it via
                <code> /{viewSystem?.command}</code> in chat.
              </SheetDescription>
            </SheetHeader>
            {viewSystem && (
              <div className="space-y-3 mt-4 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground">Command</div>
                  <code className="font-mono">/{viewSystem.command}</code>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Title</div>
                  <div>{viewSystem.title}</div>
                </div>
                {viewSystem.description && (
                  <div>
                    <div className="text-xs text-muted-foreground">Description</div>
                    <div>{viewSystem.description}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs text-muted-foreground">Content</div>
                  <pre className="rounded border bg-muted/30 p-3 text-xs whitespace-pre-wrap font-mono max-h-96 overflow-y-auto">
                    {viewSystem.content}
                  </pre>
                </div>
                {viewSystem.variables.length > 0 && (
                  <div>
                    <div className="text-xs text-muted-foreground">Variables</div>
                    <ul className="text-xs font-mono space-y-1 mt-1">
                      {viewSystem.variables.map((v) => (
                        <li key={v.name}>
                          <code>{v.name}</code> — {v.label}
                          {v.required && (
                            <Badge variant="outline" className="ml-2 text-[10px]">
                              required
                            </Badge>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </SheetContent>
        </Sheet>
      )}

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete prompt</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete{' '}
              <code className="bg-muted px-1 rounded">
                /{confirmDelete?.command}
              </code>
              ? {adapter.deleteConfirmMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
            >
              Delete prompt
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Preview pane (tenant-only; lifted from pre-refactor) ──────────────────
//
// Live preview the prompt body with substituted variables. Useful while
// authoring; the user can see exactly what the chat invocation produces.

function PromptPreviewPane({
  template,
  variables,
}: {
  template: string;
  variables: PromptVariableEntry[];
}) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const { rendered, missing } = previewPromptTemplate(template, variables, values);

  if (!template) return null;
  return (
    <div className="rounded border p-2 bg-muted/20">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-xs font-medium hover:underline"
      >
        {open ? '▾ Preview' : '▸ Preview'}{' '}
        {missing.length > 0 && (
          <Badge
            variant="outline"
            className="ml-2 text-[9px] bg-amber-500/10 text-amber-700 border-amber-500/30"
          >
            {missing.length} required missing
          </Badge>
        )}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {variables.length > 0 && (
            <VariableValueForm
              variables={variables}
              values={values}
              onChange={setValues}
            />
          )}
          <div className="rounded bg-background border p-2 text-xs whitespace-pre-wrap font-mono max-h-64 overflow-y-auto">
            {rendered || '_(empty)_'}
          </div>
        </div>
      )}
    </div>
  );
}
